import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import { getProjectState } from "@/redux/store";
import projectApi from "@/redux/api/projectApi";
import { projectActions } from "@/redux/stores/projectStore";
import { Modal } from "antd";
import { getLangCode, makeTextClean } from "@/lib/subtitle-utils";
import { canvasHolder } from "@/lib/canvas-utils";
import { usePreviewAudio } from "@/hooks/usePreviewAudio";
import { genResWithError, genResWithOK } from "@/lib/api-utils";
import { ProjectType, Caption, Scene } from "@/types/type";
import { RestApiResponse } from "@/types/common.api";
import { removeByTagName } from "@/lib/text-utils";
import { useGetUserInfoQuery } from "@/redux/api/userApi";
import { useTranslation } from "next-i18next";
import { MODAL_NAME } from "@/components/editor/stripe/constants";
import axios from "axios";

export const preproc1Subtitle = (scene: Scene) => {
  if (scene?.scripts[0]) {
    const removeGestureScript = removeByTagName(scene.scripts[0]?.org, 'gesture');
    console.log( `>>>>>> removeGestureScript `, {removeGestureScript})
    const removedEffectScript = removeByTagName(removeGestureScript, 'effect');
    console.log( `>>>>>> removedEffectScript `, {removedEffectScript})
    const text = makeTextClean(removedEffectScript);
    if (text === "") {
      throw new Error("Your model's script has not been entered");
    }
  }
}

export const preproc2Subtitle = (scene: Scene, audio:any, splitNum:number) => {
  const removeGestureScript = removeByTagName(scene.scripts[0]?.org, 'gesture');
  console.log( `>>>>>> removeGestureScript 2222`, {removeGestureScript})
  const removedEffectScript = removeByTagName(removeGestureScript, 'effect');
  console.log( `>>>>>> removedEffectScript `, {removedEffectScript})
  const params = {
    url: audio.data.downloadUrl,
    script: makeTextClean(removedEffectScript),
    langCode: getLangCode(
      scene.scripts[0].detectedLanguage ??
      scene.scripts[0].tts?.language ??
      scene.scripts[0].modelSupportLanguage
    ),
    splitNum: splitNum ?? 100,
  }

  return params
}

type SubtitleHooksProps = {
  project: ProjectType | null;
}

export function useSubtitleHooks(props: SubtitleHooksProps = {
  project: null,
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const { project: reduxProject } = useAppSelector((store) => getProjectState(store));
  const { project: propsProject } = props;
  const { data: userInfo } = useGetUserInfoQuery();


  const project = propsProject ? propsProject : reduxProject;

  const selectedSceneIdx = useAppSelector((store) => getProjectState(store).selectedSceneIdx);
  const { canvas } = canvasHolder;
  
  const { getPreviewAudio } = usePreviewAudio();
  const [llmWordTimeStamp] = projectApi.useLlmWordTimeStampMutation();

  const getGenerateSubtitle: (
    index: number,
    splitNum?: number,
    abort?: AbortSignal,
  ) => Promise<RestApiResponse<Caption[]>> = useCallback(
    async (index, splitNum, abort) => {
      try {
        if (!project || !project.scenes[index]) {
          throw new Error("project or scene is null");
        }
        if (abort?.aborted) {
          throw new Error("getGenerateSubtitle aborted");
        }
      
        const scene = project.scenes[index];

        /**
         * 오디오 스크립트일 때는 자막 생성 안 함
         */
        if (
          scene?.scripts[0]?.isTTS &&
          scene?.scripts[0]?.tts?.type === 'audio'
        ) {
            throw new Error('Cannot generate subtitles for audio scripts.')
        }
        
        // 여기 수정할 대 위에 preproc1Subtitle 도 수정 부탁드려요. for 자동화 백엔드 합성 서비스 from leokim
        if (scene?.scripts[0]) {
          const removeGestureScript = removeByTagName(scene.scripts[0]?.org, 'gesture');
          console.log( `>>>>>> removeGestureScript `, {removeGestureScript})
          const removedEffectScript = removeByTagName(removeGestureScript, 'effect');
          console.log( `>>>>>> removedEffectScript `, {removedEffectScript})
          const text = makeTextClean(removedEffectScript);
          if (text === "") {
            throw new Error(t("Your model's script has not been entered"));
          }
        }

        // 자막 요청 시에는 템포 무시
        const audio = await getPreviewAudio({
          project: {
            ...project,
            scenes: project.scenes.map((s, i) => {
              if (i === index) {
                const { tempo, ...rest } = s;
                return rest;
              }
              return s;
            })
          },
          index,
          abort
        });
    
        if (!audio.success) {
          return audio;
        }
    
        // 여기 수정할 대 위에 preproc2Subtitle 도 수정 부탁드려요. for 자동화 백엔드 합성 서비스 from leokim
        // TODO: 다화자 작업 필요
        const removeGestureScript = removeByTagName(scene.scripts[0]?.org, 'gesture');
        console.log( `>>>>>> removeGestureScript 2222`, {removeGestureScript})
        const removedEffectScript = removeByTagName(removeGestureScript, 'effect');
        console.log( `>>>>>> removedEffectScript `, {removedEffectScript})
        const params = {
          url: audio.data.downloadUrl,
          script: makeTextClean(removedEffectScript),
          langCode: getLangCode(
            scene.scripts[0].detectedLanguage ??
            scene.scripts[0].tts?.language ??
            scene.scripts[0].modelSupportLanguage
          ),
          splitNum: splitNum ?? 100,
        }

        if(scene.scripts[0]?.tts?.language === "multi") {
          const cleanedText = makeTextClean(removeGestureScript);
          try {

            const { data } = await axios.post<{
              success: boolean,
              data: {
                lang_cd: string,
                score: number
              }
            }>('/api/backend/detection', {
              apiUrl: "/api/v2/detection",
              method: "POST",
              params: { text: cleanedText }
            })
      
            if(data.success) params.langCode = data.data.lang_cd;

          } catch(error) {
            console.error('detectLanguage error', error);
          }

        }
    
        const caption = await llmWordTimeStamp(params).unwrap();
    
        if (!caption.success) {
          throw new Error("llm request failed.")
        }
        if (abort?.aborted) {
          throw new Error("getGenerateSubtitle aborted");
        }

        const tempo = Number(project.scenes[index].tempo || 1);
        const result = caption.data.map((item: Caption) => {
          return {
            ...item,
            start: Number((item.start / tempo).toFixed(2)),
            end: Number((item.end / tempo).toFixed(2)),
          }
        });
    
        return genResWithOK(result);
      } catch (e) {
        return genResWithError(e);
      }
    }, [getPreviewAudio, llmWordTimeStamp, project, t]
  );
  
  const handleGenerateSubtitle = useCallback(async () => {

    // if (!userInfo?.plan?.includes("creator")) {
    if(!userInfo?.subscriptionPermission?.includes('GENERATE_CAPTION')){
      const noticeModalPayload = {
        currentPlan: userInfo?.plan,
        openedModal: MODAL_NAME.NOTICE,
        modalTitle: t("LOCKED FEATURE"),
        modalLogoUrl: "/images/modal/mdi_lock.png",
        noticeTitle: t("You’ve discovered a locked feature"),
        noticeExplains: [
          t(
            "By By upgrading your plan, you'll unlock the ability to generate captions."
          ),
        ],
      };
      dispatch(projectActions.setNoticeModalCredit(noticeModalPayload));
      return;
    }

    dispatch(projectActions.setCaptionActions({ type: 'loading', isLoading: true }));

    /**
     * 시작 시점에 씬에 아이디를 부여하고,
     * 종료 시점에 씬에 부여된 아이디를 이용하여 캡션을 삽입한다.
     */
    const sceneIdForGeneratingCaption = Date.now().toString(32)

    try {
      if (!canvas) return;

      dispatch(projectActions.setSceneIdForGeneratingCaption({
        sceneIdForGeneratingCaption,
        sceneIndex: selectedSceneIdx,
      }))

      const captions = await getGenerateSubtitle(selectedSceneIdx);

      if (!captions.success) {
        throw new Error(
          captions.error?.msg
          || "The service is temporarily unavailable. Please try again later."
        );
      }

      if ((captions.data?.length ?? 0) < 1) {
        throw new Error("Failed to generate captions. Please try again.");
      }
      
      const watermark = canvas.getObjects().find((obj) => obj.name === 'watermark');
      
      const canvasWidth = canvas?.getWidth() / canvas.getZoom();
      const canvasHeight = canvas?.getHeight() / canvas.getZoom();
      const width = 0.5 * canvasWidth;
      const height = 35;
      const top = watermark 
      ? canvasHeight - height - (canvasHeight * 0.12) + (watermark.getScaledHeight() / 2.7) as number
      : canvasHeight - height - (canvasHeight * 0.12) as number;
      const left = (canvasWidth - width) / 2;

      const newObj = {
        text: captions.data[0].caption,
        captions: captions.data,
        type: 'captions',
        fill: '#ffffff',
        backgroundColor: '#000000',
        fontSize: 28,
        padding: 5,
        align: 'center',
        fontFamily: "Noto Sans CJK",
        width,
        height,
        top,
        left,
      };

      await dispatch(projectActions.addCaptionAsync({
        captionClip: newObj,
        sceneIdForGeneratingCaption,
      }))
      dispatch(projectActions.initCaptions())
      dispatch(projectActions.setCaptionActions({ type: 'loading', isLoading: false }));
    } catch (e: any) {
      const msg = e instanceof Error ? e.message : "The service is temporarily unavailable. Please try again later.";

      Modal.error({
        content : msg,
        onOk: () => dispatch(projectActions.setCaptionActions({ type: 'loading', isLoading: false })),
        centered: true,
      });
      console.log('>>>>>> handleGenerateSubtitle error ', {e})
    } finally {
      /**
       * 캡션 생성이 끝나면 씬에 부여된 아이디를 초기화한다.
       */
      dispatch(projectActions.removeSceneIdForGeneratingCaption({
        sceneIdForGeneratingCaption,
      }))
      dispatch(projectActions.setCaptionActions({ type: 'loading', isLoading: false }));
    }
  }, [
      canvas, 
      dispatch,
      getGenerateSubtitle,
      selectedSceneIdx,
      userInfo?.subscriptionPermission,
      userInfo?.plan,
      t,
    ]);

  return {
    getGenerateSubtitle,
    handleGenerateSubtitle
  };
}